import { EyeIcon } from '@heroicons/react/outline';
import getSimilarTransaction from 'api/v1/transactions/getSimilarTransaction';
import fileSearchIcon from 'assets/svg/icons/file-search-icon.svg';
import { Button } from 'components/atoms/Button';
import { Modal } from 'components/atoms/Modal';
import SectionSeparator from 'components/atoms/SectionSeparator.component';
import SimpleAlertV2 from 'components/atoms/SimpleAlertV2.component';
import { motion } from 'motion/react';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import useQuery from 'hooks/react-query/useQuery';

import { BankTransactionsCacheKeys } from 'types/cacheKeys.types';
import type { CurrenciesEnum } from 'types/global.types';
import type { Transaction } from 'types/transactions.types';
import { fadeTransition } from 'utils/constants';
import TransactionInformation from '../Classification/TransactionInformation.component';
import { MIN_TRANSFER_AMOUNT } from './MoneyTransfer/constants/externalTransferSchema';

const SimilarTransactionAlert: FC<{
  onConfirm: () => void;
  amount: number;
  currency: CurrenciesEnum;
  fromIBAN: string;
  toIBAN: string;
  isLoading: boolean;
}> = ({ onConfirm, amount, currency, fromIBAN, toIBAN, isLoading }) => {
  const { t } = useTranslation();

  const params = {
    amount,
    currency,
    fromIBAN,
    toIBAN,
  };

  const similarTransactionQuery = useQuery(
    [BankTransactionsCacheKeys.SimilarTransaction, params],
    () => getSimilarTransaction(params),
    {
      enabled: !!(amount >= MIN_TRANSFER_AMOUNT && fromIBAN && toIBAN),
      staleTime: 0,
    },
  );

  return (
    <Modal
      name="Payment initiation - similar transaction details"
      color="bank"
      width="2xl"
      trigger={(api) =>
        similarTransactionQuery.data?.hasSimilarTransaction ? (
          <div className="text-left">
            <SimpleAlertV2
              type="warning"
              subtitle={t(
                'accountable_banking.review_transfer.similar_transaction.content',
              )}
              extra={
                <Button asDefault onClick={() => api.openModal()}>
                  <EyeIcon className="w-6 h-6 text-yellow-800" />
                </Button>
              }
            />
          </div>
        ) : null
      }
      render={(api) => (
        <div className="px-6">
          <div className="flex flex-col items-center">
            <motion.div
              {...fadeTransition}
              className="w-10 h-10 p-1.5 bg-bank-100 rounded-full text-bank-900 stroke-bank-900 fill-bank-900"
            >
              <img src={fileSearchIcon} />
            </motion.div>

            <motion.div {...fadeTransition} className="my-4 text-center">
              <h1 className="text-2xl font-bold text-primary-800 mx-auto">
                {t('accountable_banking.review_transfer.title')}
              </h1>

              <p className="text-primary-700 mt-2">
                {t(
                  'accountable_banking.review_transfer.similar_transaction.content',
                )}
              </p>
            </motion.div>
          </div>

          <SectionSeparator className="!bg-gray-100 !my-6 !h-[1px]" />

          <div className="mt-10">
            <TransactionInformation
              transaction={
                similarTransactionQuery.data?.similarTransaction as Transaction
              }
            />
          </div>

          <div className="w-full flex space-x-4 mt-20">
            <Button
              structure="secondary"
              onClick={api.closeModal}
              className="flex-1"
              color="bank"
            >
              {t('cancel')}
            </Button>

            <Button
              onClick={onConfirm}
              loading={isLoading}
              className="flex-1"
              color="bank"
            >
              {t('confirm')}
            </Button>
          </div>
        </div>
      )}
    />
  );
};

export default SimilarTransactionAlert;
